import React, { useEffect, useState, useCallback } from "react";
import { UserContext, ProfileProvider } from "./context";
import { Routes, Route, Navigate } from "react-router-dom";
import OwlyManage from "./OwlyManage/OwlyManage";
import Authenticator from "./Authn/Authenticator";
import PrivateRoute from "./PrivateRoute";
import { getEdxUserInfo, setUserCookie, getUserCookie, initializeAutoSSO, setSSOUserCookie } from "./context/cookieUtils";
import { getCookie, getDomainCandidates, deleteCookieDynamic, deleteClientCookies } from "./utils/clientCookies";
import useUserCookieSync from "./hooks/useUserCookieSync";
import useRequestUserFromParent from "./hooks/useRequestUserFromParent";
import useCrossAppMessaging from "./hooks/useCrossAppMessaging";
import useAutoSSO from "./hooks/useAutoSSO";
import useSSOCallback from "./hooks/useSSOCallback";
import { fetchCurrentUser } from "./helpers/userApi";
import { SSOService, processCallback, hasCallbackInUrl, clearSSOFlags, enableAutoSSOForSession } from "./services/sso";
import { isTrustedMessageOrigin } from "./utils/messagingOrigins";

// Expose debug helpers for manual validation in browser console
if (typeof window !== "undefined") {
  window.owlyManageDebug = Object.assign({}, window.owlyManageDebug || {}, {
    deleteCookieDynamic,
    deleteClientCookies,
    getDomainCandidates,
    getCookie,
    getEdxUserInfo,
    checkAllCookies: () => {
      const cookies = {
        autenticator_user: !!getCookie("autenticator_user"),
        edx_user_info: !!getCookie("edx-user-info")
      };
      return cookies;
    },
    simulateLogoutEvent: () => {
      window.postMessage({
        type: "client-platform-logout",
        source: "debug-simulation",
        timestamp: Date.now(),
        reason: "manual-test"
      }, "*");
    },
    // Manual logout function for UI buttons
    manualLogout: () => {
      if (window.owlyManageManualLogout) {
        window.owlyManageManualLogout();
      } else {
        console.warn('Manual logout function not available yet');
      }
    },
    // SSO Debug helpers
    sso: {
      getStatus: () => SSOService.getSSOStatus(),
      clearAttempts: () => SSOService.clearAttempts(),
      clearFlags: () => clearSSOFlags(),
      attemptLogin: () => SSOService.attemptAutoLogin(),
      // Callback helpers
      hasCallback: () => hasCallbackInUrl(),
      processCallback: () => processCallback(),
    },
  });
}

export default function App() {
  const [userAutenticate, setUserAutenticate] = useState(() => {
    // Al iniciar, intenta leer el usuario de la cookie
    try {
      const cookie = getUserCookie();
      if (cookie) {
        return cookie;
      }
    } catch (error) {
      console.warn("Error parsing user cookie, clearing it:", error);
      deleteCookieDynamic("autenticator_user");
    }
    return null;
  });

  // Estado para rastrear si el logout fue manual (click en botón)
  const [manualLogout, setManualLogout] = useState(false);

  // Inicializar listeners/monitoreo para SSO cross-domain y sincronización de cookies
  useEffect(() => {
    initializeAutoSSO();
  }, []);

  // 1. Procesar callback del SSO si existe en la URL
  // Este hook detecta si el usuario regresó del SSO y procesa los tokens
  useSSOCallback({
    setUser: setUserAutenticate,
    enabled: !userAutenticate, // Solo si no hay usuario autenticado
    onSuccess: (userData) => {
      console.log('[App] 🎉 SSO callback successful:', userData.username);
    },
    onError: (error) => {
      console.error('[App] ❌ SSO callback error:', error.message);
    },
  });

  // Función para logout manual (desde botón UI)
  const handleManualLogout = useCallback(() => {
    // Marcar como logout manual para evitar SSO automático
    setManualLogout(true);
    
    // Limpiar estado del usuario y cookies
    try {
      setUserAutenticate(null);
      clearSSOFlags();
      
      // Limpiar cookies
      deleteCookieDynamic("autenticator_user");
      deleteClientCookies([
        "autenticator_user",
        "edx-user-info",
      ]);
      
      // Limpiar localStorage
      localStorage.removeItem("owly_user");
      sessionStorage.removeItem("owly_session");
      
      // Limpiar flags de SSO para próximas sesiones
      clearSSOFlags();
      
      // Habilitar auto SSO nuevamente (limpiar errores previos)
      enableAutoSSOForSession();
      
      // Limpiar intento de SSO de la sesión actual
      try {
        sessionStorage.removeItem('owly_session_sso_attempted');
      } catch (e) {
        // Continuar si sessionStorage no está disponible
      }
    } catch (error) {
      console.error("[App] ❌ Error during manual logout:", error);
    }
    
    // Resetear flag después de un tiempo
    setTimeout(() => {
      setManualLogout(false);
    }, 30000); // 30 segundos
  }, [setUserAutenticate]);

  // 2. Auto-login SSO basado en referer
  // Si hay referer válido, redirige automáticamente al SSO
  // PERO NO si acabamos de hacer logout manual
  useAutoSSO({
    enabled: !userAutenticate && !manualLogout, // Solo si no hay usuario Y no fue logout manual
  });

  // Exponer función de logout manual globalmente para que la pueda usar el Sidebar
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.owlyManageManualLogout = handleManualLogout;
      // Exponer también un setter seguro para actualizar el usuario autenticado
      // desde componentes profundos (por ejemplo, después de editar organización)
      window.owlyManageSetUser = (nextUser) => {
        if (!nextUser) return;
        setUserAutenticate((prev) => {
          try {
            const a = JSON.stringify(prev);
            const b = JSON.stringify(nextUser);
            return a === b ? prev : nextUser;
          } catch (_) {
            return nextUser;
          }
        });
      };
    }
  }, [handleManualLogout, setUserAutenticate]);

  // Refresh the user to reflect latest role/permissions
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (userAutenticate) {
          const updated = await fetchCurrentUser(userAutenticate);
          if (!alive || !updated) return;
          // Only update if core fields changed to avoid loops
          const coreChanged = (
            updated?.id !== userAutenticate?.id ||
            updated?.email !== userAutenticate?.email ||
            updated?.username !== userAutenticate?.username ||
            // role may be id or object with have_admin_access
            (typeof updated?.role_id === 'object' ? updated.role_id?.have_admin_access : undefined) !==
              (typeof userAutenticate?.role_id === 'object' ? userAutenticate.role_id?.have_admin_access : undefined)
          );
          if (coreChanged) {
            try { setUserCookie(updated); } catch (_) {}
            setUserAutenticate(updated);
          }
        }
      } catch (e) {
        // non-fatal
      }
    })();
    return () => { alive = false; };
  }, [userAutenticate]);

  // Helpers para debugging de postMessage
  const logPostMessageDebug = (message, data) => {
    if (typeof window !== "undefined" && window.owlyManageDebug) {
      // console.log(`[Owly Manage][PostMessage] ${message}`, data);
    }
  };

  // Listener para eventos desde owly-frontend (logout)
  useEffect(() => {
    const handleClientEvent = (event) => {
      if (!isTrustedMessageOrigin(event.origin)) {
        return;
      }

      // Petición explícita de limpieza de cookies desde el chat
      if (event.data?.type === "owly-clear-client-cookies") {
        console.log('[Owly Manage] 🍪 Cookie clear request received:', event.data.cookies);
        deleteClientCookies(event.data?.cookies);
        // No retornamos: también podemos proceder con logout interno si aplica
      }

      // Verificar que el mensaje sea de logout del cliente
      if (
        event.data?.type === "client-platform-logout" ||
        event.data?.type === "owly-client-logout" ||
        event.data?.type === "owly-logout" ||
        event.data?.action === "logout"
      ) {
        
        // Borrar cookies del cliente de forma dinámica
        deleteClientCookies([
          "autenticator_user",
          "edx-user-info",
        ]);

        // Ejecutar logout interno
        handleInternalLogout();
      } else if (event.data?.type) {
        console.log('[Owly Manage] ❌ Message ignored - not a logout event. Type:', event.data.type);
      }
    };

    window.addEventListener("message", handleClientEvent);

    return () => {
      window.removeEventListener("message", handleClientEvent);
    };
  }, []);

  // Función para ejecutar logout interno
  const handleInternalLogout = () => {
    try {
      console.log('[Owly Manage] 🧹 Starting internal logout process...');
      
      // Verificar estado de cookies antes de limpiar
      const beforeCleanup = {
        autenticator_user: !!getCookie("autenticator_user"),
        edx_user_info: !!getCookie("edx-user-info")
      };
      console.log('[Owly Manage] 🍪 Cookies before cleanup:', beforeCleanup);

      // Limpiar cookie(s) de usuario usando helper con dominio dinámico
      deleteCookieDynamic("autenticator_user");

      // También limpiar con deleteClientCookies como fallback para otras cookies
      deleteClientCookies([
        "autenticator_user",
        "edx-user-info",
      ]);

      // Limpiar estado del usuario
      setUserAutenticate(null);
      console.log('[Owly Manage] 👤 User state cleared');

      // Limpiar flags de SSO para evitar auto-login en próxima sesión
      try {
        clearSSOFlags();
        console.log('[Owly Manage] 🔐 SSO flags cleared');
      } catch (e) {
        console.warn("[Owly Manage] Error clearing SSO flags:", e);
      }

      // Limpiar localStorage si hay datos relacionados
      try {
        localStorage.removeItem("owly_user");
        sessionStorage.removeItem("owly_session");
        console.log('[Owly Manage] 💾 Storage cleared');
      } catch (e) {
        console.warn("[Owly Manage] Error clearing storage:", e);
      }

      // Verificar estado de cookies después de limpiar
      setTimeout(() => {
        const afterCleanup = {
          autenticator_user: !!getCookie("autenticator_user"),
          edx_user_info: !!getCookie("edx-user-info")
        };
        console.log('[Owly Manage] 🍪 Cookies after cleanup:', afterCleanup);
      }, 100);

      console.log('[Owly Manage] ✅ Internal logout process completed');
    } catch (error) {
      console.error("[Owly Manage] ❌ Error during internal logout:", error);
    }
  };

  // Listener adicional para BroadcastChannel (comunicación cross-tab)
  useEffect(() => {
    let channel = null;

    try {
      // BroadcastChannel para comunicación entre pestañas del mismo origen
      channel = new BroadcastChannel("owly-logout-channel");

      channel.addEventListener("message", (event) => {
        logPostMessageDebug("BroadcastChannel message received:", event.data);

        // Eventos de logout
        if (
          event.data?.type === "client-platform-logout" ||
          event.data?.type === "owly-client-logout" ||
          event.data?.type === "owly-logout" ||
          event.data?.action === "logout"
        ) {
          console.log('[Owly Manage] 🚨 BroadcastChannel LOGOUT event:', event.data);
          deleteClientCookies([
            "autenticator_user",
            "edx-user-info",
          ]);
          handleInternalLogout();
        }
      });

      logPostMessageDebug("BroadcastChannel listener setup complete", null);
    } catch (error) {
      console.warn("[Owly Manage] BroadcastChannel not supported:", error);
    }

    return () => {
      if (channel) {
        try {
          channel.close();
        } catch (error) {
          console.warn("[Owly Manage] Error closing BroadcastChannel:", error);
        }
      }
    };
  }, []);

  // Listener para localStorage events (fallback adicional)
  useEffect(() => {
    const handleStorageEvent = (event) => {
      // --- EVENTOS DE LOGOUT ---
      if (event.key === "owly-logout-event" && event.newValue) {
        try {
          const logoutData = JSON.parse(event.newValue);
          logPostMessageDebug(
            "localStorage logout event received:",
            logoutData
          );

          if (
            logoutData?.type === "client-platform-logout" ||
            logoutData?.type === "owly-client-logout" ||
            logoutData?.type === "owly-logout" ||
            logoutData?.action === "logout"
          ) {
            deleteClientCookies([
              "autenticator_user",
              "edx-user-info",
            ]);
            handleInternalLogout();
          }
        } catch (error) {
          console.warn(
            "[Owly Manage] Error parsing localStorage logout event:",
            error
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);
      logPostMessageDebug("localStorage listener setup complete", null);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  // Si no hay usuario, solicita al parent frame el usuario
  useRequestUserFromParent({ getUserCookie });

  // Sync user state with cookie periodically via hook
  useUserCookieSync({
    getUserFromCookies: getUserCookie,
    fetchCurrentUser,
    deleteCookieDynamic,
    setUserAutenticate,
    intervalMs: 2000,
  });

  // Cross-frame messaging (REQUEST_USER and user sync)
  useCrossAppMessaging({
    getUserCookie,
    setUserAutenticate,
    setSSOUserCookie,
    setUserCookie,
  });

  // Sincroniza el contexto y las rutas
  return (
    <UserContext.Provider value={userAutenticate}>
      <ProfileProvider>
        <Routes>
        {/* Ruta pública: login (también en /login) */}
        <Route
          path="/"
          element={
            userAutenticate ? (
              <Navigate to="/app" replace />
            ) : (
              <Authenticator setUser={setUserAutenticate} />
            )
          }
        />
        <Route
          path="/login"
          element={<Authenticator setUser={setUserAutenticate} />}
        />
        <Route
          path="/register"
          element={<Authenticator setUser={setUserAutenticate} />}
        />
        <Route
          path="/reset_password/:uid/:token"
          element={<Authenticator setUser={setUserAutenticate} />}
        />
        <Route
          path="/forgot_password"
          element={<Authenticator setUser={setUserAutenticate} />}
        />

        {/* Rutas privadas: todo OwlyManage y subrutas */}
        <Route
          path="/app/*"
          element={
            <PrivateRoute user={userAutenticate}>
              <OwlyManage />
            </PrivateRoute>
          }
        />

        {/* Cualquier otra ruta redirige a / */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProfileProvider>
    </UserContext.Provider>
  );
}
